import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, Lock, Sparkles, Zap, Package, QrCode, CreditCard, Clock, Mail, Phone, Copy, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Clean QR Code image
const UPI_QR_CODE = "https://customer-assets.emergentagent.com/job_taxsetu-practiq/artifacts/9rd60px6_image.png";

// Owner email for demo bypass
const OWNER_EMAIL = 'support@taxsetu.ai';
const OWNER_PASSWORD = 'Demo@2026';

const PaywallModal = ({ isOpen, onClose, feature, onSubscribe }) => {
  const navigate = useNavigate();
  const { unlockModule } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState('gst_complete_monthly');
  const [paymentStep, setPaymentStep] = useState('plans'); // 'plans', 'payment', 'utr', 'success', 'owner_auth'
  const [isLoading, setIsLoading] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [utrNumber, setUtrNumber] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Auto-select plan based on feature prop
  useEffect(() => {
    if (feature === 'gstr1') {
      setSelectedPlan('gstr1_monthly');
    } else if (feature === 'gstr3b') {
      setSelectedPlan('gstr3b_monthly');
    }
  }, [feature]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setPaymentStep('plans');
      setError('');
      setUtrNumber('');
      setOwnerPassword('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const plans = [
    {
      id: 'gst_complete_monthly',
      name: 'GST Complete',
      subtitle: 'GSTR-1 + GSTR-3B',
      price: 149,
      originalPrice: 198,
      duration: '1 Month',
      badge: 'Best Deal',
      badgeColor: 'from-amber-500 to-orange-500',
      discount: '25% OFF',
      features: [
        'Full GSTR-1 Filing Practice',
        'Full GSTR-3B Filing Practice',
        'Real-world GST Scenarios',
        'Step-by-step Guidance'
      ]
    },
    {
      id: 'gst_complete_yearly',
      name: 'GST Complete',
      subtitle: 'GSTR-1 + GSTR-3B',
      price: 999,
      originalPrice: 1188,
      duration: '1 Year',
      badge: 'Recommended',
      badgeColor: 'from-emerald-500 to-teal-500',
      discount: '16% OFF',
      features: [
        'Full GSTR-1 Filing Practice',
        'Full GSTR-3B Filing Practice',
        'Real-world GST Scenarios',
        '12 Months Unlimited Access'
      ]
    },
    {
      id: 'gstr1_monthly',
      name: 'GSTR-1 Only',
      subtitle: 'Single Module',
      price: 99,
      duration: '1 Month',
      features: [
        'GSTR-1 Filing Practice',
        'B2B & B2C Invoices',
        'Export/SEZ Supplies',
        'Credit/Debit Notes'
      ]
    },
    {
      id: 'gstr3b_monthly',
      name: 'GSTR-3B Only',
      subtitle: 'Single Module',
      price: 99,
      duration: '1 Month',
      features: [
        'GSTR-3B Filing Practice',
        'Tax Liability Calculation',
        'ITC Reconciliation',
        'Payment Challan Practice'
      ]
    }
  ];

  const selectedPlanData = plans.find(p => p.id === selectedPlan);

  const handleProceedToPayment = () => {
    setPaymentStep('payment');
  };

  const handlePayWithQR = () => {
    if (!userEmail || !userPhone) {
      setError('Please enter your email and phone number');
      return;
    }
    if (!userEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    setError('');
    
    // Check if owner email - show password authentication instead
    if (userEmail.toLowerCase() === OWNER_EMAIL.toLowerCase()) {
      setPaymentStep('owner_auth');
      return;
    }
    
    setPaymentStep('utr');
  };

  // Owner authentication handler
  const handleOwnerAuth = () => {
    if (ownerPassword === OWNER_PASSWORD) {
      // Unlock all modules for owner
      unlockModule('gst_complete');
      setPaymentStep('success');
      if (onSubscribe) {
        onSubscribe();
      }
    } else {
      setError('Invalid password. Please try again.');
      setOwnerPassword('');
    }
  };

  const handleSubmitUTR = async () => {
    if (!utrNumber || utrNumber.length < 8) {
      setError('Please enter a valid UTR/Transaction ID (minimum 8 characters)');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/payments/submit-utr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          phone: userPhone,
          plan: selectedPlan,
          planName: selectedPlanData?.name,
          planDuration: selectedPlanData?.duration,
          amount: selectedPlanData?.price,
          utrNumber: utrNumber
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Unlock the appropriate module(s) based on the selected plan
        if (selectedPlan === 'gstr1_monthly') {
          unlockModule('gstr1');
        } else if (selectedPlan === 'gstr3b_monthly') {
          unlockModule('gstr3b');
        } else if (selectedPlan === 'gst_complete_monthly' || selectedPlan === 'gst_complete_yearly') {
          unlockModule('gst_complete'); // This unlocks both GSTR-1 and GSTR-3B
        }
        
        setPaymentStep('success');
        
        // Call onSubscribe callback if provided
        if (onSubscribe) {
          onSubscribe();
        }
      } else {
        setError(data.detail || 'Failed to submit payment. Please try again.');
      }
    } catch (err) {
      console.error('Payment submission error:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyUPI = () => {
    navigator.clipboard.writeText('taxsetu@upi');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Check if this is owner bypass
  const isOwnerBypass = userEmail.toLowerCase() === OWNER_EMAIL.toLowerCase();

  // Owner Authentication Screen
  if (paymentStep === 'owner_auth') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0F1629] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-purple-900/40 to-pink-900/40 px-6 py-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Owner Access</h2>
                <p className="text-sm text-gray-400">Demo & Admin Portal</p>
              </div>
            </div>
            <button onClick={() => setPaymentStep('payment')} className="absolute top-3 right-3 text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6">
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-300">
                <strong className="text-purple-400">Owner Account Detected</strong>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Enter your admin password to unlock all modules instantly.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-gray-300 mb-2 block">Admin Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="password"
                    placeholder="Enter admin password"
                    value={ownerPassword}
                    onChange={(e) => setOwnerPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleOwnerAuth()}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 pl-10 h-12"
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <Button
                onClick={handleOwnerAuth}
                className="w-full h-12 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white font-semibold"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Unlock All Modules
              </Button>

              <p className="text-center text-xs text-gray-500">
                This will unlock GSTR-1 + GSTR-3B instantly for demo purposes.
              </p>

              {/* Forgot Password Link */}
              <div className="pt-4 border-t border-white/10 mt-4">
                <a 
                  href="mailto:support@taxsetu.ai?subject=Password%20Reset%20Request%20-%20TaxSetu%20PractiQ&body=Hi%20Team%2C%0A%0AI%20need%20to%20reset%20my%20owner%20access%20password%20for%20TaxSetu%20PractiQ.%0A%0AThank%20you!"
                  className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Forgot Password?
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success Screen
  if (paymentStep === 'success') {
    // Different success message for owner bypass
    if (isOwnerBypass) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative bg-[#0F1629] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl p-8 text-center">
            <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-purple-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">All Modules Unlocked!</h2>
            <p className="text-gray-400 mb-6">
              Welcome back! You now have full access to all GST modules for demo and showcase purposes.
            </p>
            
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-gray-300">
                <strong className="text-purple-400">Unlocked Modules:</strong>
              </p>
              <ul className="text-sm text-gray-400 mt-2 space-y-1">
                <li>✓ GSTR-1 Filing Simulator</li>
                <li>✓ GSTR-3B Filing Simulator</li>
                <li>✓ Full Demo Access</li>
              </ul>
            </div>
            
            <Button 
              onClick={onClose}
              className="w-full bg-purple-600 hover:bg-purple-500"
            >
              Start Demo
            </Button>
          </div>
        </div>
      );
    }

    // Regular user success screen
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
        <div className="relative bg-[#0F1629] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl p-8 text-center">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Payment Submitted!</h2>
          <p className="text-gray-400 mb-6">
            Your payment is being verified. You will receive your login credentials at <span className="text-teal-400">{userEmail}</span> within <span className="text-amber-400 font-semibold">24 hours</span>.
          </p>
          
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-gray-300">
              <strong className="text-amber-400">What's Next?</strong>
            </p>
            <ul className="text-sm text-gray-400 mt-2 space-y-1">
              <li>• We'll verify your payment</li>
              <li>• Your unique login credentials will be emailed</li>
              <li>• Use those credentials to access your purchased modules</li>
            </ul>
          </div>
          
          <div className="text-sm text-gray-500 mb-4">
            Questions? Email us: <a href="mailto:support@taxsetu.ai" className="text-teal-400">support@taxsetu.ai</a>
          </div>
          
          <Button 
            onClick={onClose}
            className="w-full bg-teal-600 hover:bg-teal-500"
          >
            Got it!
          </Button>
        </div>
      </div>
    );
  }

  // UTR Entry Screen
  if (paymentStep === 'utr') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0F1629] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-teal-900/40 to-cyan-900/40 px-6 py-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <QrCode className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Scan & Pay</h2>
                <p className="text-sm text-gray-400">{selectedPlanData?.name} - {selectedPlanData?.duration}</p>
              </div>
            </div>
            <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6">
            {/* QR Code Display */}
            <div className="bg-white rounded-xl p-4 mb-4 text-center">
              <img 
                src={UPI_QR_CODE} 
                alt="UPI QR Code" 
                className="w-48 h-48 mx-auto object-contain"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect fill="%23f0f0f0" width="200" height="200"/><text x="50%" y="50%" text-anchor="middle" fill="%23666" font-size="14">QR Code</text></svg>';
                }}
              />
              <p className="text-gray-800 font-semibold text-lg mt-3">₹{selectedPlanData?.price}</p>
              <p className="text-gray-500 text-sm">Scan with any UPI app</p>
            </div>

            {/* UTR Input */}
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300 mb-2 block">Enter Transaction ID / UTR Number *</Label>
                <Input
                  placeholder="e.g., 412345678901"
                  value={utrNumber}
                  onChange={(e) => setUtrNumber(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Find UTR in your payment app's transaction history
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <Button
                onClick={handleSubmitUTR}
                disabled={isLoading}
                className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-semibold"
              >
                {isLoading ? 'Submitting...' : 'Submit Payment'}
              </Button>

              <p className="text-center text-xs text-gray-500">
                Your credentials will be sent to <span className="text-teal-400">{userEmail}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Payment Method Selection Screen
  if (paymentStep === 'payment') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0F1629] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-amber-900/40 to-orange-900/40 px-6 py-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Complete Payment</h2>
                <p className="text-sm text-gray-400">{selectedPlanData?.name} - {selectedPlanData?.duration} • ₹{selectedPlanData?.price}</p>
              </div>
            </div>
            <button onClick={() => setPaymentStep('plans')} className="absolute top-3 right-3 text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6">
            {/* User Details */}
            <div className="space-y-4 mb-6">
              <div>
                <Label className="text-gray-300 mb-2 block">Email Address *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="email"
                    placeholder="your.email@example.com"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    className={`bg-white/5 border-white/10 text-white placeholder:text-gray-500 pl-10 h-12 transition-all ${
                      userEmail.toLowerCase() === OWNER_EMAIL.toLowerCase() ? 'border-purple-500/50 bg-purple-500/10' : ''
                    }`}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Login credentials will be sent here</p>
              </div>
              
              {/* Show password field smoothly when owner email detected */}
              {userEmail.toLowerCase() === OWNER_EMAIL.toLowerCase() ? (
                <div className="animate-in slide-in-from-top-2 duration-300">
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      <span className="text-sm font-semibold text-purple-400">Owner Account Detected</span>
                    </div>
                    <p className="text-xs text-gray-400">Enter your password to unlock all modules instantly.</p>
                  </div>
                  
                  <Label className="text-gray-300 mb-2 block">Admin Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      type="password"
                      placeholder="Enter admin password"
                      value={ownerPassword}
                      onChange={(e) => setOwnerPassword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleOwnerAuth()}
                      className="bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 pl-10 h-12 focus:border-purple-500"
                    />
                  </div>
                  <div className="flex justify-end mt-2">
                    <a 
                      href="mailto:support@taxsetu.ai?subject=Password%20Reset%20Request%20-%20TaxSetu%20PractiQ&body=Hi%20Team%2C%0A%0AI%20need%20to%20reset%20my%20owner%20access%20password%20for%20TaxSetu%20PractiQ.%0A%0AThank%20you!"
                      className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      Forgot Password?
                    </a>
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="text-gray-300 mb-2 block">Phone Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      type="tel"
                      placeholder="+91 9876543210"
                      value={userPhone}
                      onChange={(e) => setUserPhone(e.target.value)}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 pl-10 h-12"
                    />
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm mb-4">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            {/* Show different content based on owner email */}
            {userEmail.toLowerCase() === OWNER_EMAIL.toLowerCase() ? (
              <Button
                onClick={handleOwnerAuth}
                className="w-full h-12 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white font-semibold"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Unlock All Modules
              </Button>
            ) : (
              /* Payment Options */
              <div className="space-y-3">
                <p className="text-sm text-gray-400 font-medium">Choose Payment Method</p>
                
                {/* QR Code Option */}
                <button
                  onClick={handlePayWithQR}
                  className="w-full p-4 rounded-xl bg-teal-500/10 border border-teal-500/30 hover:bg-teal-500/20 transition-all text-left group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <QrCode className="w-6 h-6 text-teal-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-white">Pay via UPI QR Code</p>
                      <p className="text-sm text-gray-400">Scan QR and enter transaction ID</p>
                    </div>
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                      Instant
                    </Badge>
                  </div>
                </button>

                {/* Direct Payment Link (Coming Soon) */}
                <button
                  disabled
                  className="w-full p-4 rounded-xl bg-white/5 border border-white/10 text-left opacity-60 cursor-not-allowed"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-white">Pay with Card/NetBanking</p>
                      <p className="text-sm text-gray-400">Razorpay/PhonePe checkout</p>
                    </div>
                    <Badge className="bg-amber-500/20 text-amber-400 border-0">
                      <Clock className="w-3 h-3 mr-1" />
                      Coming Soon
                    </Badge>
                  </div>
                </button>
              </div>
            )}

            <p className="text-center text-xs text-gray-500 mt-4">
              Questions? Email: <a href="mailto:support@taxsetu.ai" className="text-teal-400">support@taxsetu.ai</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Plans Selection Screen (Default)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-[#0F1629] border border-white/10 rounded-2xl w-full max-w-6xl shadow-2xl">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-amber-900/40 to-orange-900/40 px-6 py-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Unlock GST Modules</h2>
            <p className="text-sm text-gray-400">Choose a plan to master GST filing</p>
          </div>
          <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Dynamic Banner */}
          <div className={`mb-4 p-2.5 rounded-lg flex items-center gap-2 ${
            selectedPlan === 'gst_complete_yearly' 
              ? 'bg-emerald-500/10 border border-emerald-500/30' 
              : selectedPlan === 'gst_complete_monthly'
              ? 'bg-amber-500/10 border border-amber-500/30'
              : 'bg-blue-500/10 border border-blue-500/30'
          }`}>
            <Package className={`w-4 h-4 flex-shrink-0 ${
              selectedPlan === 'gst_complete_yearly' 
                ? 'text-emerald-400' 
                : selectedPlan === 'gst_complete_monthly'
                ? 'text-amber-400'
                : 'text-blue-400'
            }`} />
            <span className="text-sm text-gray-300">
              {selectedPlan === 'gst_complete_monthly' && (
                <><span className="text-amber-400 font-semibold">Best Deal:</span> Get both GSTR-1 & GSTR-3B at ₹149 (Save ₹49!)</>
              )}
              {selectedPlan === 'gst_complete_yearly' && (
                <><span className="text-emerald-400 font-semibold">Recommended:</span> Get 12 months unlimited access at ₹999 (Save ₹189!)</>
              )}
              {selectedPlan === 'gstr1_monthly' && (
                <><span className="text-blue-400 font-semibold">GSTR-1 Module:</span> Practice complete GSTR-1 filing for just ₹99/month</>
              )}
              {selectedPlan === 'gstr3b_monthly' && (
                <><span className="text-blue-400 font-semibold">GSTR-3B Module:</span> Practice complete GSTR-3B filing for just ₹99/month</>
              )}
            </span>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            {plans.map((plan) => (
              <div 
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative p-4 rounded-xl cursor-pointer transition-all ${
                  selectedPlan === plan.id 
                    ? 'bg-amber-500/15 border-2 border-amber-500/60 shadow-lg shadow-amber-500/10' 
                    : 'bg-white/5 border border-white/10 hover:border-white/25 hover:bg-white/8'
                }`}
              >
                {plan.badge && (
                  <Badge className={`absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-r ${plan.badgeColor} text-white border-0 text-xs px-2 py-0.5`}>
                    <Zap className="w-3 h-3 mr-1" />{plan.badge}
                  </Badge>
                )}
                
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-base font-bold text-white">{plan.name}</h3>
                    <p className="text-xs text-teal-400 font-medium">{plan.subtitle}</p>
                  </div>
                  {plan.discount && (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-xs px-1.5 py-0.5">
                      {plan.discount}
                    </Badge>
                  )}
                </div>
                
                <div className="mb-3">
                  <span className="text-2xl font-bold text-white">₹{plan.price}</span>
                  {plan.originalPrice && (
                    <span className="text-sm text-gray-500 line-through ml-1">₹{plan.originalPrice}</span>
                  )}
                  <p className="text-xs text-gray-400">{plan.duration}</p>
                </div>

                {/* Features List */}
                <div className="space-y-1.5">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-1.5">
                      <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-300 leading-tight">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Selected Plan Summary + CTA */}
          <div className="flex items-center justify-between bg-white/5 rounded-xl p-3.5 border border-white/10">
            <div>
              <p className="text-xs text-gray-400">Selected Plan</p>
              <p className="text-base font-bold text-white">{selectedPlanData?.name} - {selectedPlanData?.duration}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-xl font-bold text-amber-400">₹{selectedPlanData?.price}</p>
                {selectedPlanData?.originalPrice && (
                  <p className="text-xs text-emerald-400">Save ₹{selectedPlanData.originalPrice - selectedPlanData.price}</p>
                )}
              </div>
              <Button
                onClick={handleProceedToPayment}
                className="h-11 px-6 text-sm bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-semibold rounded-lg"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Pay ₹{selectedPlanData?.price}
              </Button>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-gray-500 mt-3">
            Secure payment • Instant access after verification
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
